import { loadDemoDb, saveDemoDb } from "../demoDb";

const ok = (data, status = 200) => ({ data, status, statusText: "OK", headers: {} });
const notFound = (message = "Not found") => ok({ message }, 404);

function toInt(val) {
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
}

function nextId(items = []) {
  return (items || []).reduce((m, x) => Math.max(m, toInt(x?.id) || 0), 0) + 1;
}

function pick(obj, keys = []) {
  const out = {};
  keys.forEach((k) => {
    if (obj && Object.prototype.hasOwnProperty.call(obj, k)) out[k] = obj[k];
  });
  return out;
}

function parseIdFromUrl(url) {
  const parts = String(url || "").split("/");
  return toInt(parts[parts.length - 1]);
}

function parseProfileSegmentFromUrl(url) {
  const parts = String(url || "").split("/").filter(Boolean);
  const idx = parts.indexOf("profile");
  if (idx === -1 || !parts[idx + 1]) return null;
  return parts[idx + 1];
}

const DEMO_KINDERGARTEN_COUNT = 10;

function buildDemoKindergartens(count = DEMO_KINDERGARTEN_COUNT) {
  return Array.from({ length: count }, (_, i) => {
    const n = i + 1;
    return {
      id: n,
      kindergarten_name: `Тестовий садочок ${n}`,
      kindergarten_type: String(n),
      address: "",
    };
  });
}

/** Поля для «Про нас» / вік; повертає збережені демо-дані з «Дубочок» на тестові назви (одноразово). */
function hydrateOsvitaAbout(db) {
  ensureOsvitaSeed(db);
  if (!db.osvita) return;
  let changed = false;

  if (!db.osvita.demoKgTestNamesRestored) {
    db.osvita.demoKgTestNamesRestored = true;
    const legacyNames = {
      Дубочок: "Тестовий садочок 1",
      "ЗДО с.Солонка": "Тестовий садочок 2",
    };
    const toTest = (name) => legacyNames[name] || name;
    for (const k of db.osvita.kindergartens || []) {
      const n = toTest(k.kindergarten_name);
      if (n !== k.kindergarten_name) {
        k.kindergarten_name = n;
        changed = true;
      }
    }
    const touch = (row) => {
      if (!row?.kindergarten_name) return;
      const n = toTest(row.kindergarten_name);
      if (n !== row.kindergarten_name) {
        row.kindergarten_name = n;
        changed = true;
      }
    };
    for (const g of db.osvita.groups || []) touch(g);
    for (const r of db.osvita.dailyFoodCost || []) touch(r);
    for (const r of db.osvita.admins || []) touch(r);
    for (const r of db.osvita.paymentStatements || []) touch(r);
    for (const r of db.osvita.billing || []) touch(r);
  }

  for (const k of db.osvita.kindergartens || []) {
    if (k.address === undefined) {
      k.address = "";
      changed = true;
    }
  }

  for (const g of db.osvita.groups || []) {
    if (g.age_from === undefined || g.age_to === undefined) {
      if (g.group_type === "young") {
        g.age_from = 3;
        g.age_to = 4;
      } else if (g.group_type === "older") {
        g.age_from = 5;
        g.age_to = 6;
      } else {
        g.age_from = g.age_from ?? null;
        g.age_to = g.age_to ?? null;
      }
      changed = true;
    }
    const kg = (db.osvita.kindergartens || []).find((x) => String(x.kindergarten_type) === String(g.kindergarten_type));
    if (kg && g.kindergarten_id == null) {
      g.kindergarten_id = kg.id;
      changed = true;
    }
  }

  if (changed) saveDemoDb(db);
}

function resolveKindergartenRow(db, segment) {
  const list = db.osvita?.kindergartens || [];
  const id = toInt(segment);
  if (id != null) {
    const byId = list.find((k) => toInt(k.id) === id);
    if (byId) return byId;
  }
  return list.find((k) => String(k.kindergarten_type) === String(segment)) || null;
}

function ensureDemoKindergartenCount(db, count = DEMO_KINDERGARTEN_COUNT) {
  ensureOsvitaSeed(db);
  const list = db.osvita.kindergartens || [];
  const byType = new Set(list.map((k) => String(k.kindergarten_type)));
  let changed = false;
  for (const row of buildDemoKindergartens(count)) {
    if (!byType.has(row.kindergarten_type)) {
      list.push(row);
      byType.add(row.kindergarten_type);
      changed = true;
    }
  }
  if (changed) {
    db.osvita.kindergartens = list;
    saveDemoDb(db);
  }
}

function ensureOsvitaSeed(db) {
  if (!db.osvita) {
    const today = new Date().toISOString().slice(0, 10);
    db.osvita = {
      kindergartens: buildDemoKindergartens(),
      groups: [
        {
          id: 1,
          kindergarten_id: 1,
          group_number: "1",
          kindergarten_type: "1",
          group_name: "Тестова група 1",
          kindergarten_name: "Тестовий садочок 1",
          group_type: "young",
          age_from: 3,
          age_to: 4,
        },
        {
          id: 2,
          kindergarten_id: 1,
          group_number: "2",
          kindergarten_type: "1",
          group_name: "Тестова група 2",
          kindergarten_name: "Тестовий садочок 1",
          group_type: "older",
          age_from: 5,
          age_to: 6,
        },
      ],
      benefitReference: [
        { id: 1, name: "Тестова пільга 1 (50%)", size: 50, regulation: "ТЕСТ" },
        { id: 2, name: "Тестова пільга 2 (100%)", size: 100, regulation: "ТЕСТ" },
      ],
      benefits: [
        {
          id: 1,
          child_id: 1,
          benefit_percentage: 50,
          benefit_reason: "ТЕСТ",
          valid_from: today,
          valid_to: "",
          documents: "ТЕСТ",
          status: "active",
          kindergarten_type: "1",
        },
      ],
      children: [
        { id: 1, child_name: "Тестова дитина 1", parent_name: "Тестовий батько 1", phone_number: "+380000000001", group_id: 1, kindergarten_type: "1" },
        { id: 2, child_name: "Тестова дитина 2", parent_name: "Тестовий батько 2", phone_number: "+380000000002", group_id: 2, kindergarten_type: "1" },
        { id: 3, child_name: "Тестова дитина 4", parent_name: "Тестовий батько 4", phone_number: "+380000000004", group_id: 1, kindergarten_type: "2" },
      ],
      attendance: [
        {
          attendance_id: 1,
          child_id: 1,
          attendance_status: "present",
          date: today,
          kindergarten_type: "1",
        },
        {
          attendance_id: 2,
          child_id: 2,
          attendance_status: "absent",
          date: today,
          kindergarten_type: "1",
        },
        {
          attendance_id: 3,
          child_id: 3,
          attendance_status: "present",
          date: today,
          kindergarten_type: "2",
        },
      ],
      pastAttendance: [
        {
          id: 1,
          child_id: 1,
          attendance_status: "present",
          date: today,
          kindergarten_type: "1",
        },
        {
          id: 2,
          child_id: 2,
          attendance_status: "absent",
          date: today,
          kindergarten_type: "1",
        },
      ],
      dailyFoodCost: [
        {
          id: 1,
          date: today,
          kindergarten_name: "Тестовий садочок 1",
          young_group_cost: 50,
          older_group_cost: 60,
          kindergarten_type: "1",
        },
      ],
      proposals: [
        {
          id: 1,
          child_name: "Тестова дитина 3",
          parent_name: "Тестовий батько 3",
          phone_number: "+380000000003",
          group_id: 1,
          benefit_percentage: 0,
          benefit_reason: "",
          status: "pending",
          review_notes: "",
          kindergarten_type: "1",
          created_at: new Date().toISOString(),
        },
      ],
      admins: [
        {
          id: 1,
          username: "demo_educator_1",
          full_name: "Тестовий вихователь 1",
          kindergarten_name: "Тестовий садочок 1",
          group_id: 1,
          role: "educator",
        },
      ],
      paymentStatements: [
        { id: 1, month: "2026-04", kindergarten_name: "Тестовий садочок 1", group_name: "Тестова група 1", child_name: "Тестова дитина 1", amount: 0, created_at: new Date().toISOString() },
      ],
      billing: [
        {
          id: 1,
          child_name: "Тестова дитина 1",
          kindergarten_name: "Тестовий садочок 1",
          group_name: "Тестова група 1",
          debt_amount: 980.46,
          charge_amount: 0,
          advance_amount: 0,
          paid_amount: 0,
          saldo: 980.46,
          payment_month: "2026-04",
        },
        {
          id: 2,
          child_name: "Тестова дитина 2",
          kindergarten_name: "Тестовий садочок 1",
          group_name: "Тестова група 2",
          debt_amount: 726.46,
          charge_amount: 0,
          advance_amount: 0,
          paid_amount: 0,
          saldo: 726.46,
          payment_month: "2026-04",
        },
      ],
    };
  }
}

function listResponse(items = [], page = 1, limit = 16) {
  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));
  const start = (page - 1) * limit;
  const paged = items.slice(start, start + limit);
  return { items: paged, totalItems, currentPage: page, totalPages };
}

export function canHandleOsvita({ url }) {
  return String(url || "").startsWith("api/kindergarten/");
}

export function handleOsvita({ method, url, body }) {
  const m = String(method || "GET").toUpperCase();
  const u = String(url || "");
  const b = body || {};

  const db = loadDemoDb();
  ensureDemoKindergartenCount(db);
  hydrateOsvitaAbout(db);

  // ---- Kindergartens ----
  if (m === "GET" && u === "api/kindergarten/kindergartens") {
    const list = (db.osvita.kindergartens || []).map((k) => ({
      id: k.id,
      name: k.kindergarten_name,
      address: k.address || "",
      kindergarten_type: k.kindergarten_type,
    }));
    return ok(list);
  }

  // ---- Groups ----
  if (m === "POST" && u === "api/kindergarten/groups/filter") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);
    const kindergarten_type = b.kindergarten_type ? String(b.kindergarten_type) : null;
    const kn = b.kindergarten_name ? String(b.kindergarten_name).trim() : null;
    let groups = db.osvita.groups || [];
    if (kindergarten_type) {
      groups = groups.filter((g) => String(g.kindergarten_type) === kindergarten_type);
    }
    if (kn) {
      groups = groups.filter((g) => String(g.kindergarten_name || "") === kn);
    }
    return ok(listResponse(groups, page, limit));
  }
  if (m === "POST" && u === "api/kindergarten/groups") {
    const kn = (b.kindergarten_name && String(b.kindergarten_name).trim()) || "Тестовий садочок 1";
    const kgRow = (db.osvita.kindergartens || []).find((k) => k.kindergarten_name === kn);
    const kindergarten_type = kgRow ? String(kgRow.kindergarten_type) : String(b.kindergarten_type || "1");
    const kindergarten_id = kgRow ? kgRow.id : toInt(kindergarten_type);
    const item = {
      id: nextId(db.osvita.groups),
      kindergarten_id: kindergarten_id || null,
      kindergarten_name: kn,
      group_name: b.group_name || `Група ${nextId(db.osvita.groups)}`,
      group_type: b.group_type || "young",
      group_number: b.group_number || String(nextId(db.osvita.groups)),
      kindergarten_type,
      age_from: b.age_from !== undefined && b.age_from !== "" ? Number(b.age_from) : null,
      age_to: b.age_to !== undefined && b.age_to !== "" ? Number(b.age_to) : null,
    };
    db.osvita.groups.unshift(item);
    saveDemoDb(db);
    return ok(item, 201);
  }
  if (m === "PUT" && u.startsWith("api/kindergarten/groups/")) {
    const id = parseIdFromUrl(u);
    const idx = db.osvita.groups.findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    const prev = db.osvita.groups[idx];
    const merged = {
      ...prev,
      ...pick(b, [
        "kindergarten_name",
        "group_name",
        "group_type",
        "group_number",
        "kindergarten_type",
        "age_from",
        "age_to",
      ]),
    };
    if (b.age_from === null) merged.age_from = null;
    if (b.age_to === null) merged.age_to = null;
    const kgRow = (db.osvita.kindergartens || []).find((k) => k.kindergarten_name === merged.kindergarten_name);
    if (kgRow) {
      merged.kindergarten_type = String(kgRow.kindergarten_type);
      merged.kindergarten_id = kgRow.id;
    }
    db.osvita.groups[idx] = merged;
    saveDemoDb(db);
    return ok(db.osvita.groups[idx]);
  }
  if (m === "DELETE" && u.startsWith("api/kindergarten/groups/")) {
    const id = parseIdFromUrl(u);
    db.osvita.groups = db.osvita.groups.filter((x) => toInt(x.id) !== id);
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }
  if (m === "POST" && u === "api/kindergarten/groups/by-kindergarten") {
    const kindergartenName = b.kindergarten_name ? String(b.kindergarten_name) : null;
    const items = (db.osvita.groups || []).filter((g) => !kindergartenName || g.kindergarten_name === kindergartenName);
    return ok(items);
  }

  // ---- Billing ----
  if (m === "POST" && u === "api/kindergarten/billing/filter") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);

    let items = db.osvita.billing || [];
    if (b.payment_month) {
      const month = String(b.payment_month);
      items = items.filter(x => !x.payment_month || String(x.payment_month) === month);
    }
    if (b.child_name) {
      const q = String(b.child_name).toLowerCase();
      items = items.filter(x => String(x.child_name || "").toLowerCase().includes(q));
    }
    if (b.kindergarten_name) {
      const q = String(b.kindergarten_name).toLowerCase();
      items = items.filter(x => String(x.kindergarten_name || "").toLowerCase().includes(q));
    }
    if (b.group_id) {
      const gid = Number(b.group_id);
      const group = (db.osvita.groups || []).find(g => Number(g.id) === gid);
      if (group) items = items.filter(x => x.group_name === group.group_name);
    }

    return ok(listResponse(items, page, limit));
  }
  if (m === "POST" && u === "api/kindergarten/billing") {
    const item = {
      id: nextId(db.osvita.billing),
      child_name: b.child_name || "Тестова дитина",
      kindergarten_name: b.kindergarten_name || "Тестовий садочок 1",
      group_name: b.group_name || "Тестова група 1",
      debt_amount: Number(b.debt_amount || 0),
      charge_amount: Number(b.charge_amount || 0),
      advance_amount: Number(b.advance_amount || 0),
      paid_amount: Number(b.paid_amount || 0),
      saldo: Number(b.saldo || (Number(b.debt_amount || 0))),
      payment_month: String(b.payment_month || "2026-04"),
    };
    db.osvita.billing.unshift(item);
    saveDemoDb(db);
    return ok(item, 201);
  }
  if (m === "PUT" && u.startsWith("api/kindergarten/billing/")) {
    const id = parseIdFromUrl(u);
    const idx = db.osvita.billing.findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.billing[idx] = { ...db.osvita.billing[idx], ...b, id: db.osvita.billing[idx].id };
    saveDemoDb(db);
    return ok(db.osvita.billing[idx]);
  }
  if (m === "DELETE" && u.startsWith("api/kindergarten/billing/")) {
    const id = parseIdFromUrl(u);
    db.osvita.billing = db.osvita.billing.filter((x) => toInt(x.id) !== id);
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }
  if (m === "POST" && u === "api/kindergarten/billing/sync-all") {
    return ok({ ok: true, demo: true, message: "DEMO: sync-all disabled" });
  }
  if (m === "POST" && u === "api/kindergarten/billing/parse-pdf") {
    return ok({ ok: true, demo: true, message: "DEMO: parse-pdf disabled" });
  }
  if (m === "POST" && u === "api/kindergarten/billing/advance-preview") {
    return ok({ ok: true, demo: true, preview: [] });
  }
  if (m === "POST" && u === "api/kindergarten/billing/calculate-advance") {
    return ok({ ok: true, demo: true, result: [] });
  }
  if (m === "POST" && u === "api/kindergarten/billing/generate-zip") {
    return ok({ ok: true, demo: true, message: "DEMO: generate-zip disabled" });
  }
  if (m === "POST" && u === "api/kindergarten/billing/receipt-packages/upload") {
    return ok({ ok: true, demo: true, message: "DEMO: receipt-packages upload disabled" });
  }
  if (m === "GET" && u.startsWith("api/kindergarten/billing/receipt-packages/download")) {
    const emptyZip = new Uint8Array([0x50, 0x4b, 0x05, 0x06, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
    const blob = new Blob([emptyZip], { type: "application/zip" });
    return {
      data: blob,
      status: 200,
      statusText: "OK",
      headers: { "content-disposition": "attachment; filename*=UTF-8''Demo_kvitantsii.zip" },
    };
  }
  if (m === "GET" && u.startsWith("api/kindergarten/billing/receipt-packages/exists")) {
    const mG = u.match(/group_id=([^&]+)/);
    const gid = mG ? parseInt(decodeURIComponent(mG[1]), 10) : 0;
    return ok({ exists: Number.isFinite(gid) && gid === 1, demo: true });
  }
  if (m === "GET" && u.startsWith("api/kindergarten/billing/generate/")) {
    return ok({ ok: true, demo: true, message: "DEMO: generate disabled" });
  }

  // ---- Benefit reference (CRUD) ----
  if (m === "GET" && u === "api/kindergarten/benefit-reference") {
    return ok(db.osvita.benefitReference || []);
  }
  if (m === "POST" && u === "api/kindergarten/benefit-reference") {
    const item = { id: nextId(db.osvita.benefitReference), name: b.name || `Тестова пільга ${nextId(db.osvita.benefitReference)}`, size: Number(b.size || 0), regulation: b.regulation || "ТЕСТ" };
    db.osvita.benefitReference.unshift(item);
    saveDemoDb(db);
    return ok(item, 201);
  }
  if (m === "PUT" && u.startsWith("api/kindergarten/benefit-reference/")) {
    const id = parseIdFromUrl(u);
    const idx = db.osvita.benefitReference.findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.benefitReference[idx] = { ...db.osvita.benefitReference[idx], ...pick(b, ["name", "size", "regulation"]) };
    saveDemoDb(db);
    return ok(db.osvita.benefitReference[idx]);
  }
  if (m === "DELETE" && u.startsWith("api/kindergarten/benefit-reference/")) {
    const id = parseIdFromUrl(u);
    db.osvita.benefitReference = db.osvita.benefitReference.filter((x) => toInt(x.id) !== id);
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }

  // ---- Children roster ----
  if (m === "POST" && u === "api/kindergarten/childrenRoster/filter") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);
    const kindergarten_type = b.kindergarten_type ? String(b.kindergarten_type) : null;
    const children = db.osvita.children
      .filter(c => !kindergarten_type || String(c.kindergarten_type) === kindergarten_type)
      .map((c) => {
        const group = (db.osvita.groups || []).find((g) => toInt(g.id) === toInt(c.group_id));
        return {
          ...c,
          group_name: group?.group_name || "",
          kindergarten_name: group?.kindergarten_name || "",
        };
      });
    return ok(listResponse(children, page, limit));
  }
  if (m === "POST" && u === "api/kindergarten/childrenRoster") {
    const item = {
      id: nextId(db.osvita.children),
      child_name: b.child_name || `Тестова дитина ${nextId(db.osvita.children)}`,
      parent_name: b.parent_name || `Тестовий батько ${nextId(db.osvita.children)}`,
      phone_number: b.phone_number || "+380000000000",
      group_id: toInt(b.group_id) || 1,
      kindergarten_type: String(b.kindergarten_type || "1"),
    };
    db.osvita.children.unshift(item);
    saveDemoDb(db);
    return ok(item, 201);
  }
  if (m === "PUT" && u.startsWith("api/kindergarten/childrenRoster/")) {
    const id = parseIdFromUrl(u);
    const idx = db.osvita.children.findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.children[idx] = { ...db.osvita.children[idx], ...pick(b, ["child_name", "parent_name", "phone_number", "group_id"]) };
    saveDemoDb(db);
    return ok(db.osvita.children[idx]);
  }
  if (m === "DELETE" && u.startsWith("api/kindergarten/childrenRoster/")) {
    const id = parseIdFromUrl(u);
    db.osvita.children = db.osvita.children.filter((x) => toInt(x.id) !== id);
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }

  // ---- Attendance ----
  if (m === "POST" && u === "api/kindergarten/attendance/filter") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);
    const kindergarten_type = b.kindergarten_type ? String(b.kindergarten_type) : null;
    const date = b.date ? String(b.date) : null;

    // Return roster-like list: one row per child for the selected date
    const children = (db.osvita.children || []).filter((c) => !kindergarten_type || String(c.kindergarten_type) === kindergarten_type);
    const rows = children.map((c) => {
      const group = (db.osvita.groups || []).find((g) => toInt(g.id) === toInt(c.group_id));
      const attendance = (db.osvita.attendance || []).find((a) =>
        toInt(a.child_id) === toInt(c.id) &&
        (!kindergarten_type || String(a.kindergarten_type) === kindergarten_type) &&
        (!date || String(a.date) === date)
      );
      return {
        child_id: c.id,
        child_name: c.child_name,
        group_name: group?.group_name || "",
        kindergarten_name: group?.kindergarten_name || "",
        attendance_id: attendance?.attendance_id || null,
        attendance_status: attendance?.attendance_status || "absent",
        date: date || attendance?.date || null,
        kindergarten_type: kindergarten_type || c.kindergarten_type,
      };
    });

    return ok(listResponse(rows, page, limit));
  }

  // Visual-only writes: record locally and succeed
  if (m === "POST" && u === "api/kindergarten/attendance") {
    const item = { attendance_id: nextId(db.osvita.attendance), ...b, attendance_id_key: `DEMO_${Date.now()}` };
    db.osvita.attendance.unshift(item);
    saveDemoDb(db);
    return ok({ ok: true, demo: true, item }, 201);
  }
  if (m === "PUT" && u.startsWith("api/kindergarten/attendance/")) {
    const id = parseIdFromUrl(u);
    const idx = (db.osvita.attendance || []).findIndex((x) => toInt(x.attendance_id) === id || toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.attendance[idx] = { ...db.osvita.attendance[idx], ...b };
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }
  if (m === "DELETE" && u.startsWith("api/kindergarten/attendance/")) {
    const id = parseIdFromUrl(u);
    db.osvita.attendance = (db.osvita.attendance || []).filter((x) => (toInt(x.attendance_id) !== id && toInt(x.id) !== id));
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }

  // ---- Past attendance ----
  if (m === "POST" && u === "api/kindergarten/past_attendance/filter") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);
    const kindergarten_type = b.kindergarten_type ? String(b.kindergarten_type) : null;
    const month = b.month ? String(b.month) : null; // YYYY-MM
    const date = b.date ? String(b.date) : null; // YYYY-MM-DD

    const items = (db.osvita.pastAttendance || [])
      .filter((x) => !kindergarten_type || String(x.kindergarten_type) === kindergarten_type)
      .filter((x) => {
        if (date) return String(x.date) === date;
        if (month) return String(x.date || "").startsWith(month);
        return true;
      })
      .map((x) => {
        const child = (db.osvita.children || []).find((c) => toInt(c.id) === toInt(x.child_id));
        const group = child ? (db.osvita.groups || []).find((g) => toInt(g.id) === toInt(child.group_id)) : null;
        return {
          ...x,
          child_name: child?.child_name || "",
          group_name: group?.group_name || "",
          kindergarten_name: group?.kindergarten_name || "",
        };
      });

    return ok(listResponse(items, page, limit));
  }
  if (m === "POST" && u === "api/kindergarten/past_attendance/export-excel") {
    return ok({ ok: true, demo: true, message: "DEMO: export disabled" });
  }

  // ---- Daily food cost ----
  if (m === "POST" && u === "api/kindergarten/daily_food_cost/filter") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);
    return ok(listResponse(db.osvita.dailyFoodCost || [], page, limit));
  }
  if (m === "POST" && u === "api/kindergarten/daily_food_cost/breakdown") {
    return ok({ ok: true, demo: true, items: [] });
  }
  if (m === "POST" && u === "api/kindergarten/daily_food_cost/breakdown/child") {
    return ok({ ok: true, demo: true, items: [] });
  }
  if (m === "POST" && u === "api/kindergarten/daily_food_cost") {
    const item = { id: nextId(db.osvita.dailyFoodCost), ...b };
    db.osvita.dailyFoodCost.unshift(item);
    saveDemoDb(db);
    return ok(item, 201);
  }
  if (m === "PUT" && u.startsWith("api/kindergarten/daily_food_cost/")) {
    const id = parseIdFromUrl(u);
    const idx = (db.osvita.dailyFoodCost || []).findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.dailyFoodCost[idx] = { ...db.osvita.dailyFoodCost[idx], ...b };
    saveDemoDb(db);
    return ok(db.osvita.dailyFoodCost[idx]);
  }
  if (m === "DELETE" && u.startsWith("api/kindergarten/daily_food_cost/")) {
    const id = parseIdFromUrl(u);
    db.osvita.dailyFoodCost = (db.osvita.dailyFoodCost || []).filter((x) => toInt(x.id) !== id);
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }

  // ---- Proposals ----
  if (m === "POST" && u === "api/kindergarten/proposals/filter") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);
    const status = b.status ? String(b.status) : null;
    const items = (db.osvita.proposals || [])
      .filter((p) => !status || String(p.status) === status)
      .map((p) => {
        const group = (db.osvita.groups || []).find((g) => toInt(g.id) === toInt(p.group_id));
        return {
          ...p,
          group_name: group?.group_name || "",
          kindergarten_name: group?.kindergarten_name || "",
        };
      });
    return ok(listResponse(items, page, limit));
  }
  if (m === "POST" && u === "api/kindergarten/proposals") {
    const item = { id: nextId(db.osvita.proposals), status: "pending", created_at: new Date().toISOString(), review_notes: "", ...b };
    db.osvita.proposals.unshift(item);
    saveDemoDb(db);
    return ok(item, 201);
  }
  if (m === "POST" && u.includes("/approve")) {
    const id = toInt(u.split("/")[3]);
    const idx = (db.osvita.proposals || []).findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.proposals[idx] = { ...db.osvita.proposals[idx], status: "approved" };
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }
  if (m === "POST" && u.includes("/reject")) {
    const id = toInt(u.split("/")[3]);
    const idx = (db.osvita.proposals || []).findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.proposals[idx] = { ...db.osvita.proposals[idx], status: "rejected", review_notes: String(b.review_notes || "") };
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }

  // ---- Benefits ----
  if (m === "POST" && u === "api/kindergarten/benefits/filter") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);
    const items = (db.osvita.benefits || []).map((bn) => {
      const child = (db.osvita.children || []).find((c) => toInt(c.id) === toInt(bn.child_id));
      const group = child ? (db.osvita.groups || []).find((g) => toInt(g.id) === toInt(child.group_id)) : null;
      return {
        ...bn,
        child_name: child?.child_name || "",
        group_name: group?.group_name || "",
        kindergarten_name: group?.kindergarten_name || "",
      };
    });
    return ok(listResponse(items, page, limit));
  }
  if (m === "POST" && u === "api/kindergarten/benefits") {
    const item = { id: nextId(db.osvita.benefits), ...b };
    db.osvita.benefits.unshift(item);
    saveDemoDb(db);
    return ok(item, 201);
  }
  if (m === "PUT" && u.startsWith("api/kindergarten/benefits/")) {
    const id = parseIdFromUrl(u);
    const idx = (db.osvita.benefits || []).findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.benefits[idx] = { ...db.osvita.benefits[idx], ...b };
    saveDemoDb(db);
    return ok(db.osvita.benefits[idx]);
  }
  if (m === "DELETE" && u.startsWith("api/kindergarten/benefits/")) {
    const id = parseIdFromUrl(u);
    db.osvita.benefits = (db.osvita.benefits || []).filter((x) => toInt(x.id) !== id);
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }

  // ---- Payment statements ----
  if (m === "POST" && u === "api/kindergarten/payment_statements/monthly") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);
    return ok(listResponse(db.osvita.paymentStatements || [], page, limit));
  }
  if (m === "PUT" && u.startsWith("api/kindergarten/payment_statements/")) {
    const id = parseIdFromUrl(u);
    const idx = (db.osvita.paymentStatements || []).findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.paymentStatements[idx] = { ...db.osvita.paymentStatements[idx], ...b };
    saveDemoDb(db);
    return ok(db.osvita.paymentStatements[idx]);
  }
  if (m === "DELETE" && u.startsWith("api/kindergarten/payment_statements/")) {
    const id = parseIdFromUrl(u);
    db.osvita.paymentStatements = (db.osvita.paymentStatements || []).filter((x) => toInt(x.id) !== id);
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }

  // ---- Admins ----
  if (m === "POST" && u === "api/kindergarten/admins/filter") {
    const page = Number(b.page || 1);
    const limit = Number(b.limit || 16);
    return ok(listResponse(db.osvita.admins || [], page, limit));
  }
  if (m === "POST" && u === "api/kindergarten/admins") {
    const item = { id: nextId(db.osvita.admins), ...b };
    db.osvita.admins.unshift(item);
    saveDemoDb(db);
    return ok(item, 201);
  }
  if (m === "PUT" && u.startsWith("api/kindergarten/admins/")) {
    const id = parseIdFromUrl(u);
    const idx = (db.osvita.admins || []).findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    db.osvita.admins[idx] = { ...db.osvita.admins[idx], ...b };
    saveDemoDb(db);
    return ok(db.osvita.admins[idx]);
  }
  if (m === "DELETE" && u.startsWith("api/kindergarten/admins/")) {
    const id = parseIdFromUrl(u);
    db.osvita.admins = (db.osvita.admins || []).filter((x) => toInt(x.id) !== id);
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }

  // ---- Generate word by debt id (legacy) ----
  if (m === "GET" && u.startsWith("api/kindergarten/generate/")) {
    return ok({ ok: true, demo: true, message: "DEMO: generate disabled" });
  }

  return null;
}

